/**
 * SYSTEMS — the list of systems under the unified portal (single source of truth).
 *
 * Pure data; must not reference window / DOM, so it can later be moved into a
 * shared package `@amzn/msa-static-website-config` (shared by both UI and CDK).
 *
 * To add a new system: add one entry to the object below (domain resolution,
 * portal entry cards, and redirect URL building all derive from here).
 */
export interface SystemMeta {
  /** Permission system identifier; maps to the backend permission `system` field */
  key: string;
  /** Display name on the portal entry card */
  label: string;
  /** Subdomain prefix, e.g. `msa` → `msa.goh.lab126.amazon.dev` */
  subdomain: string;
  /** Portal card subtitle (optional) */
  description?: string;
  /** Portal Overview card body text (optional) */
  overview?: string;
  /** Portal card icon (emoji, optional) */
  icon?: string;
}

export const SYSTEMS = {
  msa: {
    key: "MSA",
    label: "MSA",
    subdomain: "msa",
    description: "AI-Powered Manufacturing Operations Management System",
    overview:
      "Manufacturing Smart Assistant consolidates fragmented standalone systems and layers AI/ML on top to assist teams with manufacturing operations management at contract manufacturer (CM) factories — turning disjointed data into proactive insights, automated alerts, and report generation.",
    icon: "🏭",
  },
  atlas: {
    // Must match the backend permission `system` field exactly (routeConfig
    // SystemType and PermissionManagement use "Atlas", not "ATLAS").
    key: "Atlas",
    label: "Atlas",
    subdomain: "atlas",
    description: "Knowledge & Documentation Workspace",
    overview:
      "Atlas is the knowledge and documentation workspace for operations teams — capture notes, attach supporting documents, and keep institutional knowledge searchable and organized alongside the systems it supports.",
    icon: "📚",
  },
  // foo: { key: "FOO", label: "Foo", subdomain: "foo" },  // ← add a line here in future
} as const satisfies Record<string, SystemMeta>;

export type SystemId = keyof typeof SYSTEMS; // 'msa' | 'atlas'
export type SystemKey = (typeof SYSTEMS)[SystemId]["key"]; // 'MSA' | 'Atlas'

/** The portal itself (not any specific system) */
export const PORTAL = "PORTAL" as const;

/** System to fall back to when no specific system resolves (transition period: legacy msa.* domain → MSA) */
export const DEFAULT_SYSTEM_ID: SystemId = "msa";

/** Look up a system definition by subdomain (case-insensitive) */
export function getSystemBySubdomain(subdomain: string): SystemMeta | undefined {
  const lower = subdomain.toLowerCase();
  return Object.values(SYSTEMS).find((s) => s.subdomain === lower);
}
